import { Component, Input, Output, EventEmitter, forwardRef, AfterViewInit, OnDestroy, ChangeDetectorRef, ViewChild, ElementRef } from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { SlickSleepService } from "../utils/slick-sleep.service";
import { SlickUtilsService } from "../utils/slick-utils.service";
import { SlickFunctionLockService } from "../utils/slick-function-lock.service";
import { ISlickFileModel } from "../slick-file-model/slick-file.model";
import { ISlickEmailerModel, } from "./slick-emailer.model";
import { ISlickEmailerAddressModel, SlickEmailerAddressModel } from "./slick-emailer-address.model";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { GlobalsService, SleepService } from "../../services";

declare const tinymce: any;

@Component({
	selector: 'slick-emailer',
	templateUrl: 'slick-emailer.component.html',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SlickEmailerComponent),
			multi: true,
		},
		SlickFunctionLockService
	]
})
export class SlickEmailerComponent implements AfterViewInit, OnDestroy {
	@Input("toEmailAddressList") toEmailAddressList: ISlickEmailerAddressModel[];
	@Input("showButtons") showButtons: boolean = true;
	@Input("bodyHeight") bodyHeight: string = "180px";
	@Input("isMobile") isMobile: boolean;
	@Output("toAddressSearch") toAddressSearchEmitter: EventEmitter<string> = new EventEmitter();
	@Output("sendEmailClick") sendEmailClickEmitter: EventEmitter<ISlickEmailerModel> = new EventEmitter();
	@Output("cancelEmailClick") cancelEmailClickEmitter: EventEmitter<void> = new EventEmitter<void>();

	@ViewChild("tinyMCEContainerRef") tinyMCEContainerRef: ElementRef;
	@ViewChild('emailIframeRef') emailIframeRef: ElementRef<HTMLIFrameElement>;

	emailerModel: ISlickEmailerModel;
	isSubmitted: boolean = false;

	uuid: string;
	tinyMCEUuid: string;
	tinyMCEInitialized: boolean = false;
	toEmailAddress: string;
	editor: any;
	spinnerStatus: string = null;
	safeEmailHtml: SafeHtml;
	bodyHTML: string;

	// Inline editing of the designed (non-old-template) layouts. There is no second editor: the preview
	// iframe IS the editor, so what the user types is literally the HTML that gets sent. bodyHTML stays
	// pristine as the fallback for a preview that never became editable.
	private previewIframe: HTMLIFrameElement;
	private previewDoc: Document;
	inlineEditingReady: boolean = false;

    constructor(private slickFunctionLockService: SlickFunctionLockService, private changeDetector: ChangeDetectorRef, private sanitizer: DomSanitizer) {
		this.uuid = SlickUtilsService.newGuid();
	}

	async ngAfterViewInit() {
		if (!this.emailerModel)
			return;

		if (!this.emailerModel?.useOldAccountingEmailTemplates) {
			this.safeEmailHtml = this.sanitizer.bypassSecurityTrustHtml(this.emailerModel?.body || '');
			this.bodyHTML = this.emailerModel?.body;
			setTimeout(() => this.adjustIframeSize(), 200);

		} else if (this.emailerModel) {
			await this.setBody(this.emailerModel.body)

		}
	}

	adjustIframeSize() {
		try {
			if (!this.isMobile) return;

			const iframe = this.emailIframeRef?.nativeElement;
			if (!iframe) return;

			const doc = iframe.contentDocument || iframe.contentWindow?.document;
			if (!doc || !doc.body) return;

			const updateSize = () => {
				const body = doc.body;
				const html = doc.documentElement;

				// Height: tight, so no extra space at bottom
				const contentHeight = Math.max(
					body.getBoundingClientRect().height,
					html.getBoundingClientRect().height
				);
				iframe.style.height = contentHeight ? `${contentHeight}px` : '600px';

				// Width: content width plus extra padding for horizontal scrolling
				const contentWidth = Math.max(
					body.scrollWidth,
					html.scrollWidth
				);

				// Add 20px extra space for horizontal scrolling
				iframe.style.width = contentWidth ? `${contentWidth}px` : '100%';
			};

			updateSize();
			setTimeout(updateSize, 300);
			setTimeout(updateSize, 1000);

			// Watch for DOM changes
			const observer = new MutationObserver(updateSize);
			observer.observe(doc.body, { childList: true, subtree: true });
		} catch (err) {
			console.warn('iframe resize failed:', err);
		}
	}

	// Fired by both preview iframes (mobile and desktop). srcdoc iframes are same-origin, so the
	// document inside is reachable and can be made editable in place.
	onPreviewLoad(evt: Event) {
		this.previewIframe = <HTMLIFrameElement>evt.target;
		this.enableInlineEditing();
		this.adjustIframeSize();
	}

	private enableInlineEditing() {
		this.inlineEditingReady = false;
		this.previewDoc = null;

		// The old templates edit their free-form body in TinyMCE; only the fixed layouts are edited here.
		if (this.emailerModel?.useOldAccountingEmailTemplates)
			return;

		try {
			const doc = this.previewIframe?.contentDocument || this.previewIframe?.contentWindow?.document;
			if (!doc || !doc.body)
				return;

			doc.body.setAttribute("contenteditable", "true");

			// Structure the user must not be able to retype. contenteditable="false" makes these atomic
			// rather than untouchable: a deliberate select-and-delete still removes one, but nobody
			// rewrites a payment link or a line item by clicking into it.
			// Every anchor is locked, which covers ACCEPT / PAY NOW / VIEW ESTIMATE DETAILS / review /
			// powered-by across all five layouts; data-skedit-lock marks the generated blocks that are
			// not links (see EmailTemplatesRawHTML).
			doc.querySelectorAll("a, [data-skedit-lock]")
				.forEach(el => el.setAttribute("contenteditable", "false"));

			// Editor-only chrome, tagged so getEditedBody strips it back out before the email goes.
			const style = doc.createElement("style");
			style.setAttribute("data-skedit-editor-chrome", "");
			style.textContent = 'body[contenteditable="true"]:focus { outline: 2px solid rgba(0, 159, 227, .35); outline-offset: -2px; }';
			(doc.head || doc.body).appendChild(style);

			doc.addEventListener("paste", this.onPreviewPaste);
			doc.addEventListener("click", this.onPreviewClick);

			this.previewDoc = doc;
			this.inlineEditingReady = true;
		}
		catch (err) {
			// Iframe torn down mid-load, or otherwise unreachable. Leave the preview read-only - getBody
			// falls back to the body as it was generated rather than sending nothing.
			console.warn("inline email editing unavailable:", err);
		}
	}

	// Word and Outlook paste markup that breaks a 600px table layout. Take the text only.
	private onPreviewPaste = (e: ClipboardEvent) => {
		const text = e.clipboardData?.getData("text/plain");
		if (text === null || text === undefined)
			return;

		e.preventDefault();
		this.previewDoc?.execCommand("insertText", false, text);
	};

	// The layout's own buttons are live links. Following one in place would navigate the preview away from
	// the email being composed, so open it in a new tab instead - users click ACCEPT / PAY NOW / VIEW
	// DETAILS here to check the amounts on the payment page before they send.
	private onPreviewClick = (e: MouseEvent) => {
		const anchor = <HTMLAnchorElement>(<HTMLElement>e.target)?.closest?.("a");
		if (!anchor)
			return;

		e.preventDefault();

		if (/^https?:/i.test(anchor.href))
			window.open(anchor.href, "_blank", "noopener");
	};

	// execCommand is deprecated but it is still the only way to act on the selection inside another
	// document, and it is what TinyMCE itself rode for years. Focus the iframe first or the command
	// lands on a collapsed selection and does nothing.
	formatPreview(command: string) {
		if (!this.inlineEditingReady)
			return;

		this.previewIframe?.contentWindow?.focus();
		this.previewDoc?.execCommand(command, false, null);
	}

	private getEditedBody(): string {
		if (!this.inlineEditingReady)
			return null;

		try {
			const doc = this.previewDoc;
			if (!doc || !doc.documentElement)
				return null;

			// Serialize a copy so the live preview keeps working if the send fails and they edit again.
			const html = <HTMLElement>doc.documentElement.cloneNode(true);

			html.querySelectorAll("[data-skedit-editor-chrome]").forEach(el => el.remove());
			html.querySelectorAll("[contenteditable]").forEach(el => el.removeAttribute("contenteditable"));
			html.querySelectorAll("[data-skedit-lock]").forEach(el => el.removeAttribute("data-skedit-lock"));

			// outerHTML drops the doctype. Without it Gmail and Outlook fall back to quirks mode and the
			// 600px tables render at the wrong width.
			return `<!doctype html>\n${html.outerHTML}`;
		}
		catch (err) {
			console.warn("could not read the edited email body:", err);
			return null;
		}
	}

	private teardownInlineEditing() {
		try {
			this.previewDoc?.removeEventListener("paste", this.onPreviewPaste);
			this.previewDoc?.removeEventListener("click", this.onPreviewClick);
		}
		catch { }

		this.previewDoc = null;
		this.previewIframe = null;
		this.inlineEditingReady = false;
	}

	// Reopening the dialog composes a fresh email, so any edits left in the preview from a previous open
	// have to go. Clearing srcdoc and setting it back is what forces the reload - assigning the same
	// value does nothing, and the dialog is not destroyed between opens.
	private async resetPreview() {
		if (!this.bodyHTML)
			return;

		this.teardownInlineEditing();
		this.safeEmailHtml = null;
		this.changeDetector.detectChanges();

		await SleepService.sleep();

		this.safeEmailHtml = this.sanitizer.bypassSecurityTrustHtml(this.bodyHTML);
		this.changeDetector.detectChanges();
	}

	ngOnDestroy() {
		this.teardownInlineEditing();
		this.removeTinyMCE();
	}

	propagateChange = (_: any) => { };

    // this is the initial value set to the component
	public async writeValue(obj: ISlickEmailerModel) {
        if (obj) {
            this.spinnerStatus = "reset";
            this.emailerModel = obj;

            if (!this.emailerModel.attachments)
                this.emailerModel.attachments = [];

            if (!this.emailerModel.toEmailAddresses)
                this.emailerModel.toEmailAddresses = [];
        }
        else {
			this.emailerModel = null;
			return;
        }

		if (!obj?.useOldAccountingEmailTemplates) {
			this.safeEmailHtml = this.sanitizer.bypassSecurityTrustHtml(this.emailerModel?.body || '');
			this.bodyHTML = this.emailerModel?.body;

        }
        else if(this.emailerModel){
			await this.setBody(this.emailerModel.body)
        }
    }

	// registers 'fn' that will be fired when changes are made
	// this is how we emit the changes back to the form
	public registerOnChange(fn: any) {
		this.propagateChange = fn;
	}

	// not used, used for touch input
	public registerOnTouched() { }

	toAddressSearch(searchText) {
		this.toAddressSearchEmitter.emit(searchText);
	}

	onSelectEmailAddress(emailerAddressModel: ISlickEmailerAddressModel) {
		this.emailerModel.toEmailAddresses.push(emailerAddressModel);

		this.toEmailAddress = null;
	}

	onFreeformTextSelect(text: string) {
		const freeformEmailAddress = new SlickEmailerAddressModel();
		freeformEmailAddress.displayName = text;
		freeformEmailAddress.emailAddress = text;

		this.emailerModel.toEmailAddresses.push(freeformEmailAddress);
		this.toEmailAddress = null;
	}

	deleteEmailAddress(idx: number) {
		this.emailerModel.toEmailAddresses.splice(idx, 1);
	}

	onAttachmentsChanged(attachments: ISlickFileModel[]) {
		this.emailerModel.attachments = this.emailerModel.attachments.concat(attachments);
	}

	validate() {
		this.isSubmitted = true;
		if (this.emailerModel.toEmailAddresses.length === 0)
			return false;

		return true;
	}

	getEmailAddresses(emailerModel: ISlickEmailerModel): ISlickEmailerAddressModel[] {
		let slickEmailerAddresses = emailerModel.toEmailAddresses || [];

		if (this.toEmailAddress) {
			const scrubbedToEmailAddress = this.toEmailAddress.replace(",", ";").replace(/\s/g, ";");
			const emailAddressesToAddStrings = scrubbedToEmailAddress.split(";").filter(x => x !== '');
			const emailAddressesToAdd = emailAddressesToAddStrings.map(x => {
				const slickEmailerAddressModel = new SlickEmailerAddressModel();
				slickEmailerAddressModel.contactId = 0;
				slickEmailerAddressModel.displayName = x;
				slickEmailerAddressModel.emailAddress = x;
				return slickEmailerAddressModel;
			});
			slickEmailerAddresses = slickEmailerAddresses.concat(emailAddressesToAdd);
		}

		this.toEmailAddress = null;

		return slickEmailerAddresses;
	}

	async setBody(body: string) {
		try {
			await this.slickFunctionLockService.lock("SLICK_EMAILER_SET_BODY");

			await this.initTinyMCE();
			const tinyMCE = tinymce.get(`emailer_tinymce_${this.tinyMCEUuid}`);

			if (!tinyMCE)
				console.error("tinyMCE not initialized");

			tinyMCE.setContent(body ?? "");

		}
		finally {
			this.slickFunctionLockService.release("SLICK_EMAILER_SET_BODY");
		}
	}

	getBody() {
		if (this.emailerModel.useOldAccountingEmailTemplates)
			return tinymce.get(`emailer_tinymce_${this.tinyMCEUuid}`).getContent();

		// The fixed layouts are edited in place in the preview. Fall back to the body as it was generated
		// if the preview never became editable, so a send can never go out empty.
		return this.getEditedBody() || this.bodyHTML;
	}

	onSendEmail() {
		this.emailerModel.toEmailAddresses = this.getEmailAddresses(this.emailerModel);

		if (this.validate() === false)
			return;

		this.emailerModel.body = this.getBody();
		this.spinnerStatus = "spin";
		if (this.sendEmailClickEmitter)
			this.sendEmailClickEmitter.emit(this.emailerModel);
	}

	emailSent() {
		this.spinnerStatus = "ok";
	}

	emailError() {
		this.spinnerStatus = "error";
	}

	onCancelEmail() {
		if (this.cancelEmailClickEmitter)
			this.cancelEmailClickEmitter.emit();
	}

	async dialogInit() {
		this.removeTinyMCE();

		if (!this.emailerModel || !this.emailerModel?.useOldAccountingEmailTemplates) {
			await this.resetPreview();
			return;
		}

		await this.setBody(this.emailerModel.body);
	}

	async initTinyMCE(): Promise<void> {
		if (!this.emailerModel || !this.emailerModel?.useOldAccountingEmailTemplates)
			return;

		return new Promise<void>(async (resolve) => {
			await this.slickFunctionLockService.lock("SLICK_EMAILER_INIT_TINY_MCE");

			if (this.tinyMCEUuid) {
				this.slickFunctionLockService.release("SLICK_EMAILER_INIT_TINY_MCE");
				resolve();
			}
			else {
				this.tinyMCEUuid = SlickUtilsService.newGuid();

				const tinyMCEContainer = <HTMLDivElement>this.tinyMCEContainerRef.nativeElement;
				const tinyMCETextarea = document.createElement("textarea");
				tinyMCETextarea.id = `emailer_tinymce_${this.tinyMCEUuid}`;
				tinyMCETextarea.style.minHeight = "300px";
				tinyMCETextarea.style.maxHeight = "300px";
				tinyMCEContainer.appendChild(tinyMCETextarea);

				tinymce.init({
					selector: `#emailer_tinymce_${this.tinyMCEUuid}`,
					promotion: false,
					menubar: 'edit insert format tools',
					// TinyMCE 7 names. The v5 spellings this used to carry (fontselect / fontsizeselect /
					// styleselect / fontsize_formats, and the imagetools plugin dropped in v6) are not
					// errors in v7 - unknown buttons are silently omitted, so the font, size and styles
					// dropdowns had simply been missing from this toolbar. Matches the settings pages,
					// e.g. accounting-templates.component.ts.
					plugins: 'link image code lists',
					toolbar: 'fontfamily | fontsize | styles | bold italic underline | indent outdent | bullist | alignleft aligncenter alignright | code | image',
					font_size_formats: "8px 10px 12px 14px 18px 24px 36px",
					height: "calc(100vh - 600px)",
					// No dotted guide lines. TinyMCE outlines every borderless table while editing, and an
					// accounting email is built from dozens of nested border="0" layout tables, so the body
					// came up covered in dashes. Editor-only either way: they never reach the sent email.
					visual: false,

					convert_urls: false,
					setup: (ed) => {
						ed.on('init', (args) => {

							ed.setContent((this.emailerModel) ? (this.emailerModel.body || '') : '');
							this.tinyMCEInitialized = true;
							this.slickFunctionLockService.release("SLICK_EMAILER_INIT_TINY_MCE");
							resolve();
						});
					}
				})
			}
		});
	}

	async removeTinyMCE() {
		if (this.tinyMCEUuid) {
			const tinyMCE = tinymce.get(`emailer_tinymce_${this.tinyMCEUuid}`);

			if (tinyMCE)
				tinyMCE.remove();

			const tinyMCEContainer = <HTMLDivElement>this.tinyMCEContainerRef.nativeElement;
			tinyMCEContainer.innerHTML = "";

			this.tinyMCEInitialized = false;

			this.tinyMCEUuid = null;
		}
	}
}
